import { cn } from '@/lib/utils';
import { useAppTheme } from '@/providers/theme-provider';
import { Pressable as PressableSlot } from '@rn-primitives/slot';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';
// eslint-disable-next-line no-restricted-syntax -- low-level primitive; button text uses the font-sans-* family tokens directly
import { ActivityIndicator, Pressable, Text } from 'react-native';

const buttonVariants = cva(
  'group flex-row items-center justify-center gap-2 rounded-lg active:opacity-80',
  {
    variants: {
      variant: {
        default: 'bg-primary',
        primary: 'bg-primary',
        destructive: 'bg-destructive',
        outline: 'border border-input bg-background',
        secondary: 'bg-secondary',
        ghost: 'bg-transparent',
        link: 'bg-transparent',
      },
      size: {
        default: 'h-12 px-4 py-3',
        sm: 'h-9 px-3 py-2',
        lg: 'h-14 px-6 py-4',
        icon: 'h-10 w-10',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

const buttonTextVariants = cva(
  'font-sans-semibold text-center',
  {
    variants: {
      variant: {
        default: 'text-primary-foreground',
        primary: 'text-primary-foreground',
        destructive: 'text-destructive-foreground',
        outline: 'text-foreground',
        secondary: 'text-secondary-foreground',
        ghost: 'text-foreground',
        link: 'text-primary underline',
      },
      size: {
        default: 'text-base',
        sm: 'text-sm',
        lg: 'text-lg',
        icon: '',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

type ButtonProps = React.ComponentPropsWithoutRef<typeof Pressable> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
    loading?: boolean;
    fullWidth?: boolean;
    children?: React.ReactNode;
  };

const Button = React.forwardRef<
  React.ElementRef<typeof Pressable>,
  ButtonProps
>(({ className, variant, size, asChild = false, disabled, loading, fullWidth, children, ...props }, ref) => {
  const Component = asChild ? PressableSlot : Pressable;
  const theme = useAppTheme();

  const spinnerColor = (() => {
    const v = variant || 'default';
    if (v === 'link') return theme.colors.primary;
    if (v === 'destructive') return '#FFFFFF';
    if (v === 'default' || v === 'primary') return theme.colors.background;
    return theme.colors.text; // outline, secondary, ghost
  })();

  // Render children: wrap strings in Text component
  const renderChildren = () => {
    if (loading) {
      return <ActivityIndicator size="small" color={spinnerColor} />;
    }

    if (typeof children === 'string') {
      return (
        <Text className={cn(buttonTextVariants({ variant, size }))}>
          {children}
        </Text>
      );
    }

    return children;
  };

  // A string child is the button's accessible name -- but `loading` swaps it out for
  // the spinner, so without this the name disappears exactly when a screen-reader
  // user is waiting to hear what the button is doing.
  const loadingLabel =
    loading && typeof children === 'string' ? children : undefined;

  return (
    <Component
      ref={ref}
      role="button"
      disabled={disabled || loading}
      accessibilityState={{ disabled: !!(disabled || loading), busy: !!loading }}
      className={cn(
        buttonVariants({ variant, size, className }),
        (disabled || loading) && 'opacity-50',
        fullWidth && 'w-full'
      )}
      {...props}
      accessibilityLabel={props.accessibilityLabel ?? loadingLabel}
    >
      {renderChildren()}
    </Component>
  );
});

Button.displayName = 'Button';

export { Button, buttonTextVariants, buttonVariants };
export type { ButtonProps };

